import type { ManagerDashboardStats } from '@/types';
import ConditionalLoadAlertsWidget from '@/components/dashboard/ConditionalLoadAlertsWidget';
import LoadManagementWidget from '@/components/dashboard/LoadManagementWidget';
import RecentMatchesWidget from '@/components/dashboard/RecentMatchesWidget';
import TeamHealthWidget from '@/components/dashboard/TeamHealthWidget';
import {
  DashboardHero,
  DashboardMetricStrip,
  DashboardSection,
  FixtureBrief,
  PlayerStatusList,
  PriorityList,
  ReportDigest,
  type PriorityItem,
} from '@/components/dashboard/primitives';

interface ManagerDashboardViewProps {
  data: ManagerDashboardStats;
}

export default function ManagerDashboardView({ data }: ManagerDashboardViewProps) {
  const nextFixture = data.upcomingFixtures[0];
  const priorityItems: PriorityItem[] = data.criticalAlerts.map((alert) => ({
    id: alert.id,
    title: alert.message,
    context: `${alert.type} alert - ${alert.date}`,
    severity: alert.severity === 'critical'
      ? 'critical'
      : alert.severity === 'high' || alert.severity === 'medium'
        ? 'warning'
        : 'info',
    action: {
      label: 'Review',
      to: alert.playerId ? `/players/${alert.playerId}` : alert.type === 'fixture' ? '/matches' : '/reports',
    },
  }));
  const performerItems = data.topPerformers.map((player) => ({
    id: player.id,
    name: player.name,
    value: player.rating.toFixed(1),
    status: 'Rating',
    severity: 'success' as const,
    to: `/players/${player.id}`,
  }));

  return (
    <div className="space-y-6">
      <DashboardHero
        title="Match readiness briefing"
        supportingText={nextFixture
          ? `Next fixture: ${nextFixture.opponent} in the ${nextFixture.competition}, ${nextFixture.daysUntil} days away.`
          : 'No upcoming fixture is currently scheduled.'}
        primaryValue={data.activePlayers}
        primaryLabel="Active squad"
      />

      <DashboardMetricStrip
        ariaLabel="Manager squad metrics"
        metrics={[
          { label: 'Total squad', value: data.totalPlayers },
          { label: 'Active squad', value: data.activePlayers },
          { label: 'Injured', value: data.injuredPlayers },
          { label: 'Reports in 7 days', value: data.recentReports },
        ]}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <DashboardSection
            title="Priority alerts"
            description="Squad alerts requiring review."
          >
            <PriorityList ariaLabel="Manager priority alerts" items={priorityItems} emptyTitle="No priority alerts" />
          </DashboardSection>
        </div>
        <DashboardSection title="Next fixture" description="Immediate match context.">
          <FixtureBrief fixtures={data.upcomingFixtures} />
        </DashboardSection>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <TeamHealthWidget
          metrics={data.teamHealth}
          totalPlayers={data.totalPlayers}
          injuredPlayers={data.injuredPlayers}
        />
        <LoadManagementWidget />
      </div>

      <ConditionalLoadAlertsWidget />

      <DashboardSection title="Top performers" description="Current leaders from the dashboard contract.">
        <PlayerStatusList ariaLabel="Manager top performers" items={performerItems} emptyTitle="No performer data" />
      </DashboardSection>

      <DashboardSection title="Recent intelligence" description="Latest staff reports for the squad.">
        <ReportDigest reports={data.recentReportFeed} />
      </DashboardSection>

      <RecentMatchesWidget />
    </div>
  );
}
